import classnames from 'classnames/bind'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'

import { useNavBannerContext } from '~/components/Navigation/Navigation/NavBanner'
import { StarEmptyIcon, StarFullIcon } from '~/components/UI/Icons'
import InlineCta from '~/components/UI/InlineCta'
import { ProductReviewsListProps } from '~/components/UI/ProductReviewsList'

import { useStyle } from '~/providers/StyleProvider'
import { useTheme } from '~/providers/ThemeProvider'

import useBreakpoint from '~/hooks/useBreakpoint'

import numberAsBoolean from '~/utils/number-as-boolean'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface StarRatingsProps {
  className?: string
  itemClassName?: string
  ratings?: number
  reviews?: Omit<ProductReviewsListProps, 'className' | 'innerRef'>
  productReviewsListRef?: any
  isClickable?: boolean
}

const TOTAL = 5

function StarRatings({
  className,
  itemClassName,
  ratings,
  reviews,
  productReviewsListRef,
  isClickable,
}: StarRatingsProps) {
  const { navHeight, bannerHeight } = useTheme()
  const [bannerVisible] = useNavBannerContext()
  const isMobileLayout = useBreakpoint('lg')
  const ctaStyle = useStyle({
    color: GlobalThemeColors.Blue,
  })

  const calcStarPercentage = (index) => {
    if (index <= ratings) return 100
    if (index > ratings) return (ratings - (index - 1)) * 100
    if (index > ratings + 1) return 0
  }

  const scrollToReviews = () => {
    const layoutBannerHeight = isMobileLayout
      ? parseInt(bannerHeight?.mobile ?? 0)
      : parseInt(bannerHeight?.desktop ?? 0)
    window.scrollTo({
      top:
        productReviewsListRef?.current?.getBoundingClientRect?.()?.top +
        window?.scrollY -
        (isMobileLayout
          ? parseInt(navHeight?.mobile ?? 0)
          : parseInt(navHeight?.desktop ?? 0)) -
        (bannerVisible ? layoutBannerHeight : 0),
      behavior: 'smooth',
    })
  }

  if (!reviews && !ratings) return null
  return (
    <div
      className={cx(css.StarRatings, className, { isClickable })}
      onClick={() => (isClickable ? scrollToReviews() : null)}>
      {numberAsBoolean(ratings) &&
        [...Array(TOTAL)].map((item, key) => (
          <div key={`star_${key}`} className={cx(css.star, itemClassName)}>
            <StarEmptyIcon className={cx(css.icon, css.empty, itemClassName)} />

            <div
              className={cx(css.fullIconMask)}
              style={{
                width: `${calcStarPercentage(key + 1)}%`,
              }}>
              <StarFullIcon className={cx(css.icon, css.full, itemClassName)} />
            </div>
          </div>
        ))}
      {reviews && (
        <InlineCta
          className={cx(css.reviews, ctaStyle)}
          lineProps={{
            isVisible: false,
          }}
          textPreset={
            GlobalTextPreset.Cta12PrimaryReg
          }>{`(${reviews?.productReviewsCount})`}</InlineCta>
      )}
    </div>
  )
}

export default StarRatings
